import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Printer, Pencil } from "lucide-react";
import { useAuth } from "@/AuthContext";
import { API } from "@/config";

export const TrialView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token } = useAuth();
  const [trial, setTrial] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => { fetchTrial(); fetchLogo(); }, [id]);

  const fetchTrial = async () => {
    try { const r = await axios.get(`${API}/trials/${id}`); setTrial(r.data); }
    catch (error) { toast.error("Error fetching trial agreement"); }
  };

  const fetchLogo = async () => {
    try { const r = await axios.get(`${API}/logo`, { responseType: 'blob' }); setLogoUrl(URL.createObjectURL(r.data)); }
    catch (error) { console.log("No logo"); }
  };

  const handlePrint = () => { window.print(); };

  const handleUpdateStatus = async (newStatus) => {
    try {
      await axios.put(`${API}/trials/${id}/status?status=${newStatus}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Trial marked as ${newStatus}`);
      fetchTrial();
    } catch (error) { toast.error("Error updating status"); }
  };

  if (!trial) return <div>Loading...</div>;

  const startDate = new Date(trial.trial_start).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const endDate = new Date(trial.trial_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between print:hidden">
        <h1 className="text-4xl font-bold" data-testid="trial-view-title">Trial Agreement</h1>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => navigate('/trials')} data-testid="back-to-trials-btn">Back</Button>
          {trial.status === 'active' && (
            <Button variant="outline" onClick={() => navigate(`/trials/${trial.id}/edit`)} data-testid="edit-trial-btn">
              <Pencil className="h-4 w-4 mr-2" />Edit
            </Button>
          )}
          <Button onClick={handlePrint} data-testid="print-trial-btn">
            <Printer className="h-4 w-4 mr-2" />Print Agreement
          </Button>
          {trial.status === 'active' && (
            <Button variant="outline" onClick={() => handleUpdateStatus('returned')} data-testid="mark-returned-btn" className="text-green-700 border-green-300 hover:bg-green-50">
              Mark as Returned
            </Button>
          )}
          {trial.status === 'active' && (
            <Button variant="outline" onClick={() => handleUpdateStatus('overdue')} data-testid="mark-overdue-btn" className="text-red-700 border-red-300 hover:bg-red-50">
              Mark as Overdue
            </Button>
          )}
        </div>
      </div>

      <Card className="print:shadow-none" data-testid="trial-card">
        <CardContent className="p-8">
          <div className="space-y-8 invoice-content">
            {/* Header */}
            <div className="flex justify-between items-start border-b-2 pb-6">
              <div>
                {logoUrl && <img src={logoUrl} alt="Ellantes Logo" className="h-20 mb-4" />}
                <div className="space-y-1">
                  <div className="text-xl font-bold">Ellantes LLC</div>
                  <div className="text-sm text-gray-600">1309 Coffeen Avenue</div>
                  <div className="text-sm text-gray-600">Sheridan, WY 82801</div>
                  <div className="text-sm text-gray-600">United States</div>
                  <div className="text-sm text-gray-600 mt-2">EIN: 33-3145594</div>
                </div>
              </div>
              <div className="text-right">
                <h2 className="text-2xl font-bold mb-2 leading-tight">DEMO DEVICE<br/>TRIAL AGREEMENT</h2>
                <div className="text-xl font-bold text-blue-600" data-testid="trial-number">{trial.trial_number}</div>
                <div className="text-sm text-gray-600 mt-2">Date: {new Date(trial.created_at).toLocaleDateString()}</div>
              </div>
            </div>

            {/* Customer Info */}
            <div className="grid grid-cols-2 gap-8">
              <div>
                <h3 className="font-bold text-lg mb-2">Customer ("Borrower"):</h3>
                <div data-testid="trial-customer-info">
                  <div className="font-semibold">{trial.customer_name}</div>
                  <div>{trial.customer_address}</div>
                  <div>{trial.customer_email}</div>
                  <div>{trial.customer_phone}</div>
                </div>
              </div>
              <div className="space-y-2">
                {trial.sales_agent && (
                  <div>
                    <h3 className="font-bold text-lg mb-2">Sales Agent:</h3>
                    <div className="font-semibold text-blue-600" data-testid="trial-agent">{trial.sales_agent}</div>
                  </div>
                )}
                <div className="print:hidden">
                  <h3 className="font-bold text-lg mb-2">Status:</h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    trial.status === 'active' ? 'bg-green-100 text-green-800' :
                    trial.status === 'returned' ? 'bg-blue-100 text-blue-800' :
                    trial.status === 'overdue' ? 'bg-red-100 text-red-800' :
                    'bg-purple-100 text-purple-800'
                  }`} data-testid="trial-view-status">{trial.status.charAt(0).toUpperCase() + trial.status.slice(1)}</span>
                </div>
              </div>
            </div>

            {/* Section 1: Equipment Information */}
            <div className="print-keep-together">
              <h3 className="font-bold text-lg mb-3 border-b pb-2">1. Equipment Information</h3>
              <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <div className="text-sm text-gray-600">Device Name / Model:</div>
                  <div className="font-semibold text-lg" data-testid="trial-device">{trial.device_name}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Serial Number:</div>
                  <div className="font-semibold text-lg" data-testid="trial-serial">{trial.serial_number || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Trial Period:</div>
                  <div className="font-semibold">From {startDate} to {endDate}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Estimated Full Replacement Value:</div>
                  <div className="font-bold text-lg text-red-600" data-testid="trial-value">${trial.estimated_value.toFixed(2)}</div>
                </div>
              </div>
            </div>

            {/* Section 2: Condition */}
            <div className="print-keep-together">
              <h3 className="font-bold text-lg mb-3 border-b pb-2">2. Condition of Equipment</h3>
              <p className="text-sm leading-relaxed mb-3">
                The Customer acknowledges that they have inspected the Device upon delivery and it is in good working order and "like-new" cosmetic condition. Any pre-existing scratches or functional issues must be noted in writing at the time of receipt.
              </p>
              {trial.pre_existing_notes && (
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                  <div className="text-sm font-semibold mb-1">Pre-Existing Notes:</div>
                  <div className="text-sm" data-testid="trial-preexisting">{trial.pre_existing_notes}</div>
                </div>
              )}
              {!trial.pre_existing_notes && (
                <div className="border-2 border-dashed border-gray-300 p-4 rounded-lg">
                  <div className="text-sm text-gray-500 italic">Pre-existing conditions (if any): _______________________________________________</div>
                </div>
              )}
            </div>

            {/* Section 3: Operational Responsibility */}
            <div className="print-keep-together">
              <h3 className="font-bold text-lg mb-3 border-b pb-2">3. Operational Responsibility & Safety</h3>
              <div className="space-y-3 text-sm leading-relaxed">
                <p><strong>Certified Use Only:</strong> The Customer warrants that the Device will only be operated by licensed and certified staff who have received proper training on this specific device.</p>
                <p><strong>Patient Safety:</strong> The Customer assumes full responsibility for all clinical outcomes. Ellantes is not liable for any injury, burns, or adverse reactions resulting from the use of the device during the trial period.</p>
                <p><strong>Intended Use:</strong> The device must be used only for its intended medical/aesthetic purposes.</p>
              </div>
            </div>

            {/* Section 4: Liability */}
            <div className="print-keep-together">
              <h3 className="font-bold text-lg mb-3 border-b pb-2">4. Liability & Protection for Ellantes</h3>
              <div className="space-y-3 text-sm leading-relaxed">
                <p><strong>Full Responsibility:</strong> The Customer is 100% responsible for the Device from the moment of delivery until it is officially checked back in by an Ellantes representative.</p>
                <p><strong>Loss or Damage:</strong> If the device is lost, stolen, or damaged (beyond normal wear and tear), the Customer agrees to pay the Full Replacement Value of <strong className="text-red-600">${trial.estimated_value.toFixed(2)}</strong> listed in Section 1.</p>
                <p><strong>Indemnification:</strong> The Customer agrees to defend and hold Ellantes harmless from any claims, lawsuits, or damages (including legal fees) arising from the Customer's use or operation of the Device.</p>
              </div>
            </div>

            {/* Section 5: Insurance */}
            <div className="print-keep-together">
              <h3 className="font-bold text-lg mb-3 border-b pb-2">5. Insurance Requirement</h3>
              <p className="text-sm leading-relaxed">
                The Customer agrees to maintain professional liability and property insurance that covers the Device during the trial period.
              </p>
            </div>

            {/* Section 6: Return Terms */}
            <div className="print-keep-together">
              <h3 className="font-bold text-lg mb-3 border-b pb-2">6. Return Terms</h3>
              <p className="text-sm leading-relaxed">
                The Device must be returned in the same condition as received, including all accessories (handpieces, goggles, power cords). A cleaning or refurbishment fee may be applied if the device is returned in an unsatisfactory state.
              </p>
            </div>

            {/* Additional Notes */}
            {trial.notes && (
              <div className="print-keep-together">
                <h3 className="font-bold text-lg mb-3 border-b pb-2">Additional Notes</h3>
                <p className="text-sm bg-gray-50 p-4 rounded-lg" data-testid="trial-view-notes">{trial.notes}</p>
              </div>
            )}

            {/* Signature Section */}
            <div className="mt-12 pt-8 border-t-2 border-gray-800 signature-section print-keep-together">
              <h3 className="font-bold text-lg mb-6">ACKNOWLEDGMENT & ACCEPTANCE</h3>
              <p className="mb-8 text-sm leading-relaxed">
                By signing below, the Customer acknowledges that they have read, understood, and agree to all the terms and conditions set forth in this Demo Device Trial Agreement. The Customer accepts full responsibility for the equipment described above during the trial period.
              </p>

              <div className="grid grid-cols-2 gap-12 mt-8">
                <div>
                  <div className="font-semibold text-sm mb-6 text-gray-600">CUSTOMER / BORROWER</div>
                  <div className="border-b-2 border-gray-800 mb-2 h-16"></div>
                  <div className="text-sm font-semibold">Signature</div>
                  <div className="mt-6 border-b-2 border-gray-800 mb-2 h-10"></div>
                  <div className="text-sm font-semibold">Print Name</div>
                  <div className="mt-6 border-b-2 border-gray-800 mb-2 h-10"></div>
                  <div className="text-sm font-semibold">Title / Position</div>
                  <div className="mt-6 border-b-2 border-gray-800 mb-2 h-10"></div>
                  <div className="text-sm font-semibold">Date</div>
                </div>
                <div>
                  <div className="font-semibold text-sm mb-6 text-gray-600">ELLANTES LLC (Representative)</div>
                  <div className="border-b-2 border-gray-800 mb-2 h-16"></div>
                  <div className="text-sm font-semibold">Signature</div>
                  <div className="mt-6 border-b-2 border-gray-800 mb-2 h-10"></div>
                  <div className="text-sm font-semibold">Print Name</div>
                  <div className="mt-6 border-b-2 border-gray-800 mb-2 h-10"></div>
                  <div className="text-sm font-semibold">Title / Position</div>
                  <div className="mt-6 border-b-2 border-gray-800 mb-2 h-10"></div>
                  <div className="text-sm font-semibold">Date</div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center text-xs text-gray-500 mt-8 pt-4 border-t">
              <p>Ellantes LLC - 1309 Coffeen Avenue, Sheridan, WY 82801 - EIN: 33-3145594</p>
              <p className="mt-1">This agreement is binding upon execution by both parties.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
